<!-- 
  EventListV2.vue
  이벤트 목록 관리 페이지
  
  - 이벤트 목록 조회 및 통계 페이지 이동 기능
-->
<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div class="row justify-content-center">
      <div class="col-sm-12 col-xs-12">
        <div class="card-header pb-0 col-sm-12 d-flex mainDiv">
          <div class="col-sm-12 col-xs-12">
            <div class="col-sm-12 col-xs-12 titleDiv CertListTitle justify-content-between title-area">
              <div>
                <h3>이벤트 목록</h3>
                <h5>이벤트를 관리하고 통계를 조회할 수 있습니다.</h5>
              </div>
            </div>

            <div class="card-body px-0 pt-0 pb-2 table-with-search">
              <div class="search-filter-container table-info-filter">
                <div class="search-filter-row">
                  <div class="search-filter-title">
                    검색 조건
                  </div>
                  <div class="search-filter-content">
                    <div class="mobile-wrap">
                      <a-select 
                        v-model:value="searchParams.status" 
                        size="small"
                        style="width: 150px"
                      >
                        <a-select-option value="">전체</a-select-option>
                        <a-select-option value="preparing">준비중</a-select-option>
                        <a-select-option value="ongoing">진행중</a-select-option>
                        <a-select-option value="completed">종료됨</a-select-option>
                      </a-select>
                      <a-input 
                        placeholder="이벤트명을 입력하세요"
                        v-model:value="searchParams.keyword"
                        @keyup.enter="searchEvents()"
                        size="small"
                      >
                        <template #prefix>
                          <div class="search-icon">
                            <i class="fa-regular fa-magnifying-glass" />
                          </div>
                        </template>
                      </a-input>
                    </div>
                    <div class="mobile-wrap">
                      <a-button
                        class="black"
                        size="small"
                        @click="searchEvents"
                      >
                        검색
                      </a-button>
                      <a-button
                        @click="resetSearch"
                        size="small"
                      >
                        초기화
                      </a-button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="col-xs-12 table-info-filter">
                <div class="total-div">
                  <p>
                    전체 <span class="number-highlight">{{ formatCommaNumber(totalItems) }}</span>건
                  </p>
                </div>
                <div class="search-container">
                  <a-button
                    class="legacy-stats-btn"
                    size="small"
                    @click="goToLegacyDashboard"
                  >
                    과거 이벤트 통계
                  </a-button>
                  <a-select 
                    style="width: 150px"
                    v-model:value="pageSize" 
                    @change="handlePageSizeChange"
                  >
                    <a-select-option :value="10">10개씩 보기</a-select-option>
                    <a-select-option :value="20">20개씩 보기</a-select-option>
                    <a-select-option :value="50">50개씩 보기</a-select-option>
                  </a-select>
                </div>
              </div>
              
              <a-table 
                class="ant-table-striped"
                size="small"
                :columns="columns"
                :data-source="eventList"
                :pagination="false"
                :loading="loading"
                v-if="eventList.length > 0"
              >
                <template #bodyCell="{ column, text, record }">
                  <!-- 이벤트명 -->
                  <template v-if="column.dataIndex === 'eventTitle'">
                    <div class="event-title" :title="text">{{ text }}</div>
                  </template>
                  
                  <!-- 이벤트 기간 -->
                  <template v-if="column.dataIndex === 'eventPeriod'">
                    {{ formatDate(record.startDate) }} ~ {{ formatDate(record.endDate) }}
                  </template>
                  
                  <!-- 상태 표시 -->
                  <template v-if="column.dataIndex === 'status'">
                    <span :class="['status-badge', `status-${record.status}`]">{{ getStatusText(record.status) }}</span>
                  </template>
                  
                  <!-- 작업 버튼 -->
                  <template v-if="column.dataIndex === 'actions'">
                    <div class="action-cell">
                      <a-button 
                        type="link" 
                        size="small"
                        @click="handleViewStatistics(record)"
                        style="padding: 0"
                      >
                        통계
                      </a-button>
                    </div>
                  </template>
                </template>
              </a-table>
              
              <div class="no-result-container" v-else>
                <i class="fa-solid fa-exclamation-triangle" />
                <span>등록된 이벤트가 없습니다.</span>
              </div>
            </div>
            
            <!-- 페이지네이션 -->
            <div class="pagination-container" v-if="!loading && eventList.length > 0">
              <a-pagination
                v-model:current="currentPage"
                :total="totalItems"
                :page-size="pageSize"
                show-size-changer
                show-quick-jumper
                @change="handlePageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import eventsApis from './api';
import axios from '@/axios';
import { formatCommaNumber } from '@/utils/utils';

export default {
  name: "EventListV2",
  setup() {
    const router = useRouter();
    const store = useStore();
    const loading = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const totalItems = ref(0);
    const eventList = ref([]);
    
    // API 인스턴스
    const apiInstance = ref(null);
    
    // 검색 매개변수
    const searchParams = reactive({
      keyword: '',
      status: ''
    });
    
    // 테이블 열 정의
    const columns = [
      { 
        title: '번호', 
        dataIndex: 'index', 
        key: 'index', 
        align: 'center', 
        width: 70
      },
      { 
        title: '이벤트명', 
        dataIndex: 'eventTitle', 
        key: 'eventTitle', 
        align: 'left',
        width: 300
      },
      { 
        title: '이벤트 기간', 
        dataIndex: 'eventPeriod', 
        key: 'eventPeriod', 
        align: 'center', 
        width: 200
      },
      { 
        title: '상태', 
        dataIndex: 'status', 
        key: 'status', 
        align: 'center', 
        width: 100
      },
      { 
        title: '관리', 
        dataIndex: 'actions', 
        key: 'actions', 
        align: 'center', 
        width: 120,
        fixed: 'right'
      }
    ];
    
    // 날짜 형식화 함수
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return dayjs(dateString).format('YYYY-MM-DD');
    };
    
    // 상태 텍스트 반환
    const getStatusText = (status) => {
      const statusMap = {
        preparing: '준비중',
        ongoing: '진행중',
        completed: '종료됨'
      };
      return statusMap[status] || status;
    };
    
    // API 데이터를 테이블 형식으로 매핑
    const mapApiDataToTableFormat = (apiData) => {
      if (!apiData || !apiData.items) return [];
      
      return apiData.items.map((event, index) => ({
        key: event._id,
        id: event._id,
        index: (currentPage.value - 1) * pageSize.value + index + 1,
        eventTitle: event.eventTitle || '제목 없음',
        startDate: event.startDate,
        endDate: event.endDate,
        status: event.eventStatus || 'preparing'
      }));
    };
    
    // 이벤트 목록 조회
    const fetchEvents = async () => {
      loading.value = true;
      
      try {
        if (!apiInstance.value) {
          message.error('서버 연결에 필요한 인스턴스가 준비되지 않았습니다.');
          return;
        }
        
        // API 호출 매개변수 구성
        const params = {
          page: currentPage.value,
          limit: pageSize.value,
          keyword: searchParams.keyword || undefined,
          status: searchParams.status || undefined
        };
        
        // API 호출
        await new Promise((resolve, reject) => {
          apiInstance.value.getEvents(
            params,
            (response) => {
              console.log('이벤트 목록 조회 성공:', response);
              const apiData = response.data;
              eventList.value = mapApiDataToTableFormat(apiData);
              totalItems.value = apiData.total || 0;
              resolve();
            },
            (error) => {
              console.error('이벤트 목록 조회 실패:', error);
              message.error('이벤트 목록을 불러오는 중 오류가 발생했습니다.');
              reject(error);
            }
          );
        });
      } catch (error) {
        console.error('이벤트 목록 조회 중 예외 발생:', error);
      } finally {
        loading.value = false;
      }
    };
    
    // 검색 함수
    const searchEvents = () => {
      currentPage.value = 1;
      fetchEvents();
    };
    
    // 검색 초기화
    const resetSearch = () => {
      searchParams.keyword = '';
      searchParams.status = '';
      fetchEvents();
    };
    
    // 페이지 변경 핸들러
    const handlePageChange = (page) => {
      currentPage.value = page;
      fetchEvents();
    };
    
    // 페이지 크기 변경 핸들러
    const handlePageSizeChange = () => {
      currentPage.value = 1;
      fetchEvents();
    };
    
    // 이벤트 통계 보기
    const handleViewStatistics = (record) => {
      router.push(`/events-v2/dashboard/${record.id}`);
    };
    
    // 과거 이벤트 통계 페이지로 이동
    const goToLegacyDashboard = () => {
      router.push('/events/dashboard');
    };
    
    // 마운트 시 실행
    onMounted(() => {
      // API 인스턴스 초기화
      apiInstance.value = eventsApis(axios, store);
      
      // 이벤트 목록 조회
      fetchEvents();
    });
    
    return {
      loading,
      currentPage,
      pageSize,
      totalItems,
      eventList,
      searchParams,
      columns,
      formatDate,
      getStatusText,
      searchEvents,
      resetSearch,
      handlePageChange,
      handlePageSizeChange,
      handleViewStatistics,
      goToLegacyDashboard,
      formatCommaNumber
    };
  }
};
</script>

<style scoped lang="scss">
// 검색 필터 스타일
.search-filter-content {
  .mobile-wrap {
    display: flex;
    gap: 8px;
  }
}

@media screen and (max-width: 767px) {
  .container-fluid .row.justify-content-center {
    padding: 14px 14px 36px 14px !important;
  }
  .mainDiv {
    width: 100% !important;
  }
  .search-filter-title {
    margin-right: auto;
    width: auto;
    margin-left: 10px;
  }
  .search-filter-content .mobile-wrap {
    width: 100%;
  }
  .search-filter-row {
    height: auto;
  }
  .search-filter-row,
  .search-filter-content {
    flex-direction: column;
    flex: none;
  }
  .card-header {
    margin: 0;
  }
}

@media screen and (min-width: 768px) and (max-width: 1023px) {
  .search-filter-title {
    width: 100%;
    justify-content: flex-start;
    margin-left: 20px;
  }

  .table-with-search .table-info-filter {
    flex-direction: column;
    width: 100%;
    .total-div {
      margin-right: auto;
    }
    .search-container {
      width: 100%;
      flex-direction: row;
    }
  }
  .search-filter-row {
    flex-direction: column;
    height: auto;
  }
}

.event-title {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-preparing {
  background-color: #fff7e6;
  color: #fa8c16;
  border: 1px solid #ffd591;
}

.status-ongoing {
  background-color: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.status-completed {
  background-color: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.action-cell {
  display: flex;
  justify-content: center;
  gap: 5px;
}

.dropdown-btn {
  display: flex;
  align-items: center;
  gap: 4px;
}

.dropdown-item {
  display: block;
  padding: 2px 0;
}

.pagination-container {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

.legacy-stats-btn {
  margin-right: 10px;
}
</style>